/**
 * Hardware Uploader & Global Connection Manager
 *
 * Uses the Web Serial API (Chrome / Edge 89+) to communicate with
 * hardware devices. Manages the continuous read loop and broadcasts data.
 *
 * Supported workflows:
 *   1. Arduino  — compile via a local server, then flash .hex over STK500
 *   2. MicroPython — paste script directly into the REPL (Ctrl-E → paste → Ctrl-D)
 */

export const BOARD_CONFIGS = [
    {
        id: 'arduino_uno',
        label: 'Arduino Uno',
        type: 'arduino',
        fqbn: 'arduino:avr:uno'
    },
    {
        id: 'arduino_nano',
        label: 'Arduino Nano',
        type: 'arduino',
        fqbn: 'arduino:avr:nano:cpu=atmega328'
    },
    {
        id: 'arduino_mega',
        label: 'Arduino Mega',
        type: 'arduino',
        fqbn: 'arduino:avr:mega:cpu=atmega2560'
    },
    {
        id: 'rpi_pico',
        label: 'Raspberry Pi Pico',
        type: 'micropython',
        fqbn: null
    },
    {
        id: 'esp32',
        label: 'ESP32 (MicroPython)',
        type: 'micropython',
        fqbn: null
    }
];

const COMPILE_SERVER_URL = process.env.HARDWARE_AGENT_URL ||
    window.HARDWARE_AGENT_URL ||
    'http://localhost:20557';

class HardwareUploader extends EventTarget {
    constructor() {
        super();
        /** @type {SerialPort|null} */
        this._port = null;
        this._reader = null; // Used during upload or readLoop
        this._writer = null; // Used during upload

        // Continuous read loop state
        this._keepReading = false;
        this._readableStreamClosed = null;

        this._connected = false;
        this._baudRate = 115200;
        this._isChangingBaudRate = false;

        /** @type {'arduino'|'micropython'|null} */
        this._deviceType = null;
        this._boardFqbn = 'arduino:avr:uno';
    }

    // ── public getters ──────────────────────────────────────────────

    get isConnected() {
        return this._connected;
    }
    get deviceType() {
        return this._deviceType;
    }
    get boardFqbn() {
        return this._boardFqbn;
    }
    get baudRate() {
        return this._baudRate;
    }
    get port() {
        return this._port;
    }

    // ── device selection ────────────────────────────────────────────

    setDeviceType(type, fqbn) {
        this._deviceType = type;
        if (fqbn) this._boardFqbn = fqbn;
    }

    setBaudRate(baudRate) {
        this._baudRate = parseInt(baudRate, 10);
    }

    async changeBaudRate(newBaudRate) {
        const parsedBaud = parseInt(newBaudRate, 10);
        if (this._baudRate === parsedBaud) return;

        if (!this._connected) {
            this.setBaudRate(parsedBaud);
            return;
        }

        if (this._isChangingBaudRate) return;
        this._isChangingBaudRate = true;
        this.dispatchEvent(new Event('baudRateChanging'));

        const previousBaudRate = this._baudRate;
        this._baudRate = parsedBaud;

        await this._stopReadLoop();

        try {
            await this._port.close();
        } catch (e) { }

        try {
            await this._port.open({ baudRate: this._baudRate });
            this._startReadLoop();
        } catch (e) {
            // Attempt rollback
            this._baudRate = previousBaudRate;
            try {
                await this._port.open({ baudRate: this._baudRate });
                this._startReadLoop();
            } catch (recoveryError) {
                this.disconnect();
            }
            throw new Error(`Failed to switch to ${parsedBaud} baud. The previous connection has been restored.`);
        } finally {
            this._isChangingBaudRate = false;
            this.dispatchEvent(new Event('baudRateChanged'));
        }
    }

    // ── connection ──────────────────────────────────────────────────

    async connect(baudRate = null) {
        if (!('serial' in navigator)) {
            throw new Error('Web Serial API is not supported in this browser. Please use Chrome or Microsoft Edge.');
        }

        if (baudRate) this.setBaudRate(baudRate);

        try {
            if (!this._port) {
                this._port = await navigator.serial.requestPort();
            } else {
                try {
                    await this.disconnect();
                } catch (e) { } // Ensure clean slate
            }

            await this._port.open({ baudRate: this._baudRate });
            this._connected = true;
            this.dispatchEvent(new Event('connect'));

            this._startReadLoop();
        } catch (err) {
            this._connected = false;
            this._port = null;
            throw new Error(`Failed to connect: ${err.message}`);
        }
    }

    async disconnect() {
        await this._stopReadLoop();

        try {
            if (this._port) {
                await this._port.close().catch(() => { });
            }
        } catch (_) { }

        this._connected = false;
        this._port = null;
        this.dispatchEvent(new Event('disconnect'));
    }

    // ── read loop ───────────────────────────────────────────────────

    async _startReadLoop() {
        if (!this._port || !this._connected) return;

        this._keepReading = true;
        const textDecoder = new TextDecoderStream();

        this._readableStreamClosed = this._port.readable.pipeTo(textDecoder.writable).catch(e => {
            console.warn('pipeTo stream closed with error:', e?.message || String(e));
        });

        this._reader = textDecoder.readable.getReader();

        try {
            while (this._keepReading) {
                const { value, done } = await this._reader.read();
                if (done) break;
                if (value) {
                    this.dispatchEvent(new CustomEvent('data', { detail: value }));
                }
            }
        } catch (error) {
            if (this._keepReading) {
                console.error('Read error', error);
                this.dispatchEvent(new CustomEvent('error', { detail: error?.message || String(error) }));
                this.disconnect();
            }
        } finally {
            if (this._reader) {
                this._reader.releaseLock();
                this._reader = null;
            }
        }
    }

    async _stopReadLoop() {
        this._keepReading = false;
        if (this._reader) {
            await this._reader.cancel().catch(() => { });
            this._reader = null;
        }
        if (this._readableStreamClosed) {
            await this._readableStreamClosed.catch(() => { });
            this._readableStreamClosed = null;
        }
    }

    // ── writing ─────────────────────────────────────────────────────

    async write(text) {
        if (!this._port || !this._connected) return;

        try {
            const encoder = new TextEncoder();
            const data = encoder.encode(text);
            const writer = this._port.writable.getWriter();
            await writer.write(data);
            writer.releaseLock();
        } catch (e) {
            console.error('Failed to write to port', e);
            throw e;
        }
    }

    // ── upload ──────────────────────────────────────────────────────

    async upload(code, libraries = [], onProgress = () => { }) {
        if (!this._connected || !this._port) {
            throw new Error('No device connected. Please connect first.');
        }

        this.dispatchEvent(new Event('uploadStart'));

        // Stop read loop so we can access raw port during upload
        await this._stopReadLoop();

        try {
            await this._port.close();
        } catch (e) { }

        // For Arduino, we don't open the port here because the backend agent will own it during upload.
        // For MicroPython, we still need to open it here because we upload via Web Serial.
        if (this._deviceType === 'micropython') {
            try {
                await this._port.open({ baudRate: 115200 });
            } catch (e) {
                throw new Error(`Failed to open port for MicroPython upload: ${e.message}`);
            }
        }

        try {
            if (this._deviceType === 'micropython') {
                return await this._uploadMicroPython(code, onProgress);
            }
            return await this._uploadArduino(code, libraries, onProgress);
        } finally {
            try {
                await this._port.close();
            } catch (e) { }

            // Reopen at user's desired baud rate and resume reading
            try {
                await this._port.open({ baudRate: this._baudRate });
                this._startReadLoop();
            } catch (e) {
                this.disconnect();
            }
            this.dispatchEvent(new Event('uploadEnd'));
        }
    }

    // ── Arduino workflow ────────────────────────────────────────────

    async _uploadArduino(code, libraries, onProgress) {
        onProgress(5, 'Identifying device COM port...');
        
        let targetPortName = null;
        try {
            const portInfo = this._port.getInfo();
            const res = await fetch(`${COMPILE_SERVER_URL}/compile/ports`);
            if (res.ok) {
                const data = await res.json();
                const ports = Array.isArray(data) ? data : (data.detected_ports || []);
                const matchedPort = ports.find(p => 
                    p.port && 
                    p.port.properties && 
                    p.port.properties.vid && 
                    p.port.properties.pid && 
                    parseInt(p.port.properties.vid, 16) === portInfo.usbVendorId &&
                    parseInt(p.port.properties.pid, 16) === portInfo.usbProductId
                );
                
                if (matchedPort && matchedPort.port && matchedPort.port.address) {
                    targetPortName = matchedPort.port.address;
                } else {
                    return { success: false, message: `Debug: No match found. portInfo=${JSON.stringify(portInfo)}, backend=${JSON.stringify(ports)}` };
                }
            } else {
                return { success: false, message: `Debug: res.ok is false, status=${res.status}` };
            }
        } catch (err) {
            console.warn('Failed to fetch port list from hardware agent', err);
        }

        if (!targetPortName) {
            return {
                success: false,
                message: 'Failed to identify the COM port for the device. Please make sure the AIPNT Hardware Agent is running.'
            };
        }

        onProgress(10, `Found device on ${targetPortName}. Releasing local port lock...`);
        // We already closed the port in `upload()`, but ensure a brief wait for Windows to release the handle
        await this._sleep(500);

        try {
            onProgress(20, 'Sending code to compile server...');
            
            const response = await fetch(`${COMPILE_SERVER_URL}/compile/upload`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    code,
                    fqbn: this._boardFqbn,
                    libraries,
                    port: targetPortName
                })
            });

            const result = await response.json();

            // Emit deep diagnostics regardless of success
            if (result.details || (result.compileCommand && result.uploadCommand)) {
                const data = result.details || result;
                
                this.dispatchEvent(new CustomEvent('diagnostic', {
                    detail: { type: 'info', message: `Selected Processor: ${this._boardFqbn}` }
                }));
                this.dispatchEvent(new CustomEvent('diagnostic', {
                    detail: { type: 'info', message: `COM Port: ${targetPortName}` }
                }));
                this.dispatchEvent(new CustomEvent('diagnostic', {
                    detail: { type: 'info', message: `Compile Command: ${data.compileCommand}` }
                }));
                this.dispatchEvent(new CustomEvent('diagnostic', {
                    detail: { type: 'info', message: `Upload Command: ${data.uploadCommand}` }
                }));
                
                if (data.uploadOutput) {
                    this.dispatchEvent(new CustomEvent('diagnostic', {
                        detail: { type: 'info', message: `avrdude Output:\n${data.uploadOutput}` }
                    }));
                }
            }

            if (!response.ok) {
                return {
                    success: false,
                    message: `Upload failed: ${result.error || response.statusText}`
                };
            }

            onProgress(100, `Upload successful! (${result.uploadDuration}ms)`);
            return { success: true, message: 'Upload complete!' };

        } catch (err) {
            return {
                success: false,
                message:
                    'Cannot reach the local compile server at ' +
                    `${COMPILE_SERVER_URL}. Is it running?\n${err.message}`
            };
        }
    }

    // ── MicroPython workflow ────────────────────────────────────────

    async _uploadMicroPython(code, onProgress) {
        onProgress(10, 'Entering paste mode…');

        const writer = this._port.writable.getWriter();
        const encoder = new TextEncoder();

        try {
            await writer.write(new Uint8Array([0x03]));
            await this._sleep(200);
            await writer.write(new Uint8Array([0x03]));
            await this._sleep(200);

            await writer.write(new Uint8Array([0x05]));
            await this._sleep(300);

            onProgress(30, 'Sending Python code…');

            const chunkSize = 256;
            const codeBytes = encoder.encode(code);
            for (let i = 0; i < codeBytes.length; i += chunkSize) {
                const chunk = codeBytes.slice(i, i + chunkSize);
                await writer.write(chunk);
                await this._sleep(50);

                const pct = 30 + Math.round(((i + chunkSize) / codeBytes.length) * 60);
                onProgress(Math.min(pct, 90), 'Sending Python code…');
            }

            await writer.write(new Uint8Array([0x04]));
            await this._sleep(200);

            onProgress(100, 'Upload complete!');
            return { success: true, message: 'Code uploaded successfully!' };
        } catch (err) {
            return { success: false, message: `Upload failed: ${err.message}` };
        } finally {
            writer.releaseLock();
        }
    }

    // ── helpers ──────────────────────────────────────────────────────

    _getUploadConfig() {
        if (this._deviceType === 'micropython') {
            return {};
        }

        if (this._deviceType === 'arduino') {
            return BOARD_CONFIGS.find(b => b.fqbn === this._boardFqbn) || BOARD_CONFIGS[0];
        }

        throw new Error('Please select a device before connecting.');
    }



    _sleep(ms) {
        return new Promise(resolve => setTimeout(resolve, ms));
    }
}

// Singleton
const hardwareUploader = new HardwareUploader();
export default hardwareUploader;
export { HardwareUploader };
